import { APP_CONFIG } from "@/data/constants";
import { BUSINESS_INFO } from "@/lib/config/business";

/** 고객센터 운영 시간 (크몽/숨고형 안내) */
export const SUPPORT_HOURS = {
  weekdays: "10:30~18:00",
  lunch: "13:00~14:00",
  weekend: "주말 · 공휴일 휴무",
  responseSla: "영업일 기준 24시간 내 답변",
} as const;

export const SUPPORT_HOURS_LABEL = `${SUPPORT_HOURS.weekdays} (점심시간 ${SUPPORT_HOURS.lunch}) · ${SUPPORT_HOURS.weekend}`;

export const SUPPORT_PLATFORM_NOTICE =
  "커넥톤은 AI 음악 분석·프로 피드백·레슨을 제공하는 음악 성장 플랫폼입니다. 결제·환불·배정 관련 문의는 고객센터로 남겨 주세요.";

export const SUPPORT_EMAIL = BUSINESS_INFO.email || APP_CONFIG.SUPPORT_EMAIL;
export const SUPPORT_KAKAO_URL = APP_CONFIG.KAKAO_CHANNEL_URL;
export const SUPPORT_KAKAO_HANDLE = `@${APP_CONFIG.KAKAO_CHANNEL_ID}`;

export type SupportAudience = "customer" | "pro";

export type SupportCategory = {
  value: string;
  label: string;
  audience: SupportAudience | "both";
};

export const SUPPORT_CATEGORIES: SupportCategory[] = [
  { value: "payment", label: "결제/환불", audience: "customer" },
  { value: "feedback", label: "프로 피드백", audience: "customer" },
  { value: "realtime", label: "실시간 1:1 피드백", audience: "customer" },
  { value: "lesson", label: "레슨", audience: "customer" },
  { value: "account", label: "계정/회원", audience: "both" },
  { value: "settlement", label: "정산/출금", audience: "pro" },
  { value: "assignment", label: "피드백 배정/작성", audience: "pro" },
  { value: "profile", label: "프로 프로필", audience: "pro" },
  { value: "report", label: "신고/제재", audience: "both" },
  { value: "suggestion", label: "제안/개선", audience: "both" },
  { value: "other", label: "기타", audience: "both" },
];

export function getSupportCategoriesFor(audience: SupportAudience) {
  return SUPPORT_CATEGORIES.filter(
    c => c.audience === audience || c.audience === "both"
  );
}

export function getSupportCategoryLabel(value: string): string {
  return SUPPORT_CATEGORIES.find(c => c.value === value)?.label || value;
}

export type SupportFaq = {
  id: number;
  question: string;
  answer: string;
  tags: string[];
};

export const SUPPORT_FAQS: SupportFaq[] = [
  {
    id: 1,
    question: "회원가입은 어떻게 하나요?",
    answer:
      "상단 '로그인'에서 구글·카카오·네이버 또는 이메일로 가입할 수 있습니다.",
    tags: ["계정", "가입"],
  },
  {
    id: 2,
    question: "AI 음악 분석은 어떻게 이용하나요?",
    answer:
      "AI 음악분석 메뉴에서 오디오를 업로드하면 음정·리듬·표현력 등을 분석한 리포트를 받을 수 있습니다.",
    tags: ["AI", "분석"],
  },
  {
    id: 3,
    question: "프로 피드백은 어떻게 받나요?",
    answer:
      "프로 피드백에서 분야를 선택해 신청하면, 배정된 프로가 문서형 피드백을 제공합니다. 진행 상태는 '피드백 신청내역'에서 확인할 수 있습니다.",
    tags: ["피드백", "프로"],
  },
  {
    id: 4,
    question: "현재 카드 결제가 가능한가요?",
    answer:
      "프로 피드백은 나이스페이 카드 결제가 가능합니다. 결제 완료 후 신청이 접수됩니다. 환불·오류는 고객센터 또는 카카오톡으로 문의해 주세요.",
    tags: ["결제", "PG", "환불"],
  },
  {
    id: 5,
    question: "레슨은 어떻게 신청하나요?",
    answer:
      "베타 기간에는 레슨 신청·결제를 카카오톡 채널 상담으로 안내하고 있습니다. 레슨 페이지에서 '상담문의'로 진행해 주세요.",
    tags: ["레슨", "상담"],
  },
  {
    id: 6,
    question: "실시간 1:1 피드백은 어떻게 이용하나요?",
    answer:
      "실시간 피드백은 초대를 받은 뒤 강의실에 입장합니다. 공개 테스트 강의실은 안내에 따라 로그인 후 입장할 수 있습니다.",
    tags: ["실시간", "1:1"],
  },
  {
    id: 7,
    question: "프로 정산·출금은 어디서 하나요?",
    answer:
      "프로 스튜디오에서 출금 요청을 남길 수 있습니다. 계좌 정보(은행·계좌번호·예금주)를 정확히 입력해 주세요. 문의는 프로용 고객지원으로 접수해 주세요.",
    tags: ["프로", "정산", "출금"],
  },
  {
    id: 8,
    question: "계정을 삭제하려면 어떻게 하나요?",
    answer:
      "마이페이지 > 계정·보안에서 계정 삭제를 요청할 수 있습니다. 진행 중인 피드백·레슨이 있으면 완료 후 삭제가 가능합니다.",
    tags: ["계정", "탈퇴"],
  },
];

export type SupportNotice = {
  id: string;
  title: string;
  date: string;
  href?: string;
};

/** 정적 공지 (추후 Firestore announcements로 교체 가능) */
export const SUPPORT_NOTICES: SupportNotice[] = [
  {
    id: "pg-live",
    title: "카드 결제(나이스페이) 오픈 — 프로 피드백 신청 시 결제됩니다",
    date: "2026.08",
    href: "/contact?category=payment",
  },
  {
    id: "realtime-test",
    title: "실시간 피드백 공개 테스트 운영 안내",
    date: "2026.07",
    href: "/realtime-feedback",
  },
];

export type InquiryStatus =
  | "pending"
  | "in_progress"
  | "resolved"
  | "refunded";

export const INQUIRY_STATUS_LABEL: Record<InquiryStatus, string> = {
  pending: "접수",
  in_progress: "처리 중",
  resolved: "완료",
  refunded: "환불",
};
